import { z } from "zod";

/**
 * How a worker record entered the system. Workers with `PRN_ROSTER` were imported
 * via the PRN Roster and are not real Clipboard workers — they must not be contacted.
 */
export const WORKER_SOURCE = {
  prnRoster: "PRN_ROSTER",
} as const;

export type WorkerSource = (typeof WORKER_SOURCE)[keyof typeof WORKER_SOURCE];

/** #proj-clipboard-roster — the internal Slack channel referenced by the roster do-not-contact banner. */
export const ROSTER_SLACK_CHANNEL_URL = "https://clipboardhealth.slack.com/archives/C0B06TNF8SW";

// Slack channel handle (technical identifier, not translatable copy).
const I18N_SKIP_ROSTER_SLACK_CHANNEL_NAME = "#proj-clipboard-roster";
export const ROSTER_SLACK_CHANNEL_NAME = I18N_SKIP_ROSTER_SLACK_CHANNEL_NAME;

export const agentProfileSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  qualification: z.string().optional(),
  profileImageUrl: z.string().nullish(),
  source: z.string().nullish(),
  sex: z.enum(["male", "female", "other", "unknown"]).optional(),
  dob: z.string().optional(),
  // Additional fields that the facility-specific endpoint may return
  matchingFacilityQualifications: z.array(z.string()).optional(),
  licensesData: z
    .array(
      z.object({
        _id: z.string(),
        multiState: z.boolean(),
        expiresAt: z.string().nullish(),
        qualification: z.string(),
        state: z.string(),
      })
    )
    .optional(),
});

export type AgentProfile = z.infer<typeof agentProfileSchema>;

export type WorkerSex = NonNullable<AgentProfile["sex"]>;

export interface UpdateWorkerParams {
  agentId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  rate?: number;
  locations?: string[];
  salesforceId?: string;
  availNote?: string;
  fullSocialSecurityNumber?: string;
  profileVideoUrl?: string;
  performedBy?: string;
  isTestAccount?: boolean;
  qualification?: string;
  adminUserId?: string;
  licensedStates?: string[];
  licenseExpires?: string;
  note?: string;
  experience?: string[];
  experienceType?: string;
  dob?: string;
  employmentStatus?: string;
  sex?: WorkerSex;
}
